import { mapGetters, mapState } from 'vuex'
import { aclMixin, smsMixin } from 'src/plugins/mixins'
import { RCS_SEGMENT_BYTES } from 'src/constants/rcs'
import { CONCATENATED_SEGMENT_CHARS, SINGLE_SEGMENT_CHARS, TOLL_FREE_CONCATENATED_SEGMENT_CHARS } from 'src/constants/sms'

export default {
  data () {
    return {
      charactersPerPage: 160
    }
  },

  mixins: [
    aclMixin,
    smsMixin
  ],

  computed: {
    ...mapState('auth', [
      'profile'
    ]),

    ...mapState('broadcast', [
      'selectedCampaign',
      'smartEncodedMessageLength',
      'contactsLength'
    ]),

    ...mapState('broadcast', {
      rcsChannelSelected: 'sendAsRcs'
    }),

    ...mapGetters('contacts', [
      'messageComposer'
    ]),

    canUseBroadcast () {
      const isCompanyEnabled = this.profile.bulk_rvm_enabled || this.profile.bulk_sms_enabled
      const isUserEnabled = this.hasPermissionTo('create broadcast message') || this.hasPermissionTo('create broadcast rvm')

      if (!isUserEnabled) {
        return false
      }

      return isCompanyEnabled
    },

    useMmsRate () {
      return this.messageComposer.sms.attachments.length > 0 || this.messageComposer.sms.gif_url.length > 0
    },

    hasTollFreePhoneNumber () {
      // RCS never leaves over one of the line's toll-free numbers, so toll-free pricing and its
      // warnings do not apply to an RCS broadcast.
      if (this.isRcsBroadcast) {
        return false
      }

      return this.selectedCampaign?.has_tollfree_pn
    },

    localSmsRate () {
      return this.profile.rate.local_sms
    },

    localMmsRate () {
      return this.profile.rate.local_mms
    },

    tollfreeSmsRate () {
      return this.profile.rate.tollfree_sms
    },

    tollfreeMmsRate () {
      return this.profile.rate.tollfree_mms
    },

    rvmRate () {
      return this.profile.rate.rvm
    },

    rcsRate () {
      return this.profile.rate.rcs
    },

    rcsMediaRate () {
      return this.profile.rate.rcs_media
    },

    // An RCS line sends over its rcs: sender exactly as authored: the send pipeline never converts
    // a long RCS message to MMS, so none of the MMS pricing, segment collapsing or warnings below
    // apply to it.
    //
    // Picking the RCS channel is enough — flipping the channel clears the chosen line, so waiting
    // for one to be picked would quote the broadcast at SMS rates in the meantime.
    isRcsBroadcast () {
      return this.rcsChannelSelected || !!this.selectedCampaign?.is_rcs_enabled
    },

    // RCS measures the body in UTF-8 bytes. Mirrors smartEncodedMessageLength in also counting the
    // opt-out phrase, which rides along on the same message.
    rcsMessageBytes () {
      const encoder = new TextEncoder()
      const body = this.messageComposer?.sms?.body || ''

      return encoder.encode(body).length + (this.isOptoutActive ? encoder.encode(this.optoutText).length : 0)
    },

    rcsSegmentCount () {
      return Math.ceil(this.rcsMessageBytes / RCS_SEGMENT_BYTES)
    },

    sendLongMessagesAsMms () {
      if (this.isRcsBroadcast) {
        return false
      }

      return this.selectedCampaign?.send_long_messages_as_mms || (this.selectedCampaign?.id && this.currentCompany.send_long_messages_as_mms)
    },

    shouldApplyMmsRate () {
      if (this.isRcsBroadcast) {
        return false
      }

      return this.useMmsRate || this.sendLongMessagesAsMms
    },

    showMessageSentAsMmsDueToAssets () {
      // Media on an RCS broadcast goes out as RCS media, not MMS.
      if (this.isRcsBroadcast) {
        return false
      }

      return this.useMmsRate && !this.sendLongMessagesAsMms
    },

    showMessageSentAsMmsWarning () {
      return this.shouldApplyMmsRate && !this.hasTollFreePhoneNumber
    },

    showMessageSentFromTollFreeNumberWarning () {
      return !this.shouldApplyMmsRate && this.hasTollFreePhoneNumber
    },

    showMessageSentFromTollFreeNumberAsMmsWarning () {
      return this.shouldApplyMmsRate && this.hasTollFreePhoneNumber
    },

    shouldShowWarning () {
      return this.showMessageSentAsMmsWarning || this.showMessageSentFromTollFreeNumberWarning || this.showMessageSentFromTollFreeNumberAsMmsWarning
    },

    hasRvmEnabled () {
      return this.currentCompany.bulk_rvm_enabled && !this.currentCompany.is_trial
    },

    hasSmsEnabled () {
      return this.currentCompany.bulk_sms_enabled
    }
  },

  methods: {
    messageCount () {
      const messageLength = this.smartEncodedMessageLength

      if (messageLength === 0) {
        this.segments = 0
        this.charactersPerPage = 160
        return this.segments
      } else if (this.shouldApplyMmsRate || (this.isRcsBroadcast && this.useMmsRate)) {
        // Media rides an RCS message as a single billable unit, the same way it does on MMS.
        this.segments = 1
        this.charactersPerPage = 1600
        return this.segments
      } else if (this.isRcsBroadcast) {
        // RCS text bills in flat 160-byte segments, without SMS's multi-segment headers or its
        // collapse to 70 characters on a body carrying an emoji.
        this.segments = this.rcsSegmentCount
        this.charactersPerPage = RCS_SEGMENT_BYTES
        return this.segments
      }

      // A single segment holds 160 GSM-7 / 70 UCS-2 characters. Past that, segment headers leave
      // 153 / 67 per segment, or 152 / 66 from a toll-free number.
      // @see https://www.twilio.com/docs/glossary/what-sms-character-limit
      const encoding = this.hasUnicode ? 'UCS_2' : 'GSM_7'
      const concatenatedChars = this.hasTollFreePhoneNumber ? TOLL_FREE_CONCATENATED_SEGMENT_CHARS : CONCATENATED_SEGMENT_CHARS
      const charactersPerSinglePage = SINGLE_SEGMENT_CHARS[encoding]
      const charactersPerMultiplePages = concatenatedChars[encoding]

      if (messageLength <= charactersPerSinglePage) {
        this.segments = 1
        this.charactersPerPage = charactersPerSinglePage
      } else {
        this.segments = Math.ceil(messageLength / charactersPerMultiplePages)
        this.charactersPerPage = charactersPerMultiplePages
      }

      return this.segments
    },

    getEstimatedPrice () {
      let messageRate = this.localSmsRate
      if (this.isRcsBroadcast) {
        messageRate = this.useMmsRate ? this.rcsMediaRate : this.rcsRate
      } else if (this.hasTollFreePhoneNumber && this.shouldApplyMmsRate) {
        messageRate = this.tollfreeMmsRate
      } else if (this.hasTollFreePhoneNumber) {
        messageRate = this.tollfreeSmsRate
      } else if (this.shouldApplyMmsRate) {
        messageRate = this.localMmsRate
      } else if (this.type === 'rvm') {
        messageRate = this.rvmRate
      }

      return this.contactsLength * this.messageCount() * messageRate
    },

    generateWarningMessage () {
      if (this.showMessageSentFromTollFreeNumberWarning) {
        return 'The selected line is configured with a Toll-free Number, which may lead to higher-than-expected charges for this broadcast.'
      }

      if (this.showMessageSentAsMmsDueToAssets) {
        return 'The message will be sent via MMS because it has an attachment or GIF attached to it, which may lead to higher-than-expected charges for this broadcast.'
      }

      if (this.showMessageSentAsMmsWarning) {
        return 'The selected line is configured to send long messages via MMS, which may lead to higher-than-expected charges for this broadcast.'
      }

      if (this.showMessageSentFromTollFreeNumberAsMmsWarning) {
        return 'The selected line is configured with a Toll-free Number and to send long messages via MMS which may lead to higher-than-expected charges for this broadcast.'
      }

      return ''
    }
  }
}
